const base = ''

async function apiFetch(path: string, init?: RequestInit) {
  const res = await fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.headers as Record<string, string>),
    },
  })
  const text = await res.text()
  let json: unknown = null
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    json = { raw: text }
  }
  if (!res.ok) {
    const err = json as {
      error?: string
      details?: unknown
      summary?: { total?: number; succeeded?: number; failed?: number; failures?: Array<{ reason?: string }> }
    }
    const summary = err.summary
    const summaryText =
      summary && typeof summary.total === 'number'
        ? ` (已處理 ${summary.succeeded ?? 0}/${summary.total} 張；失敗 ${summary.failed ?? 0} 張${
            summary.failures?.length ? `：${summary.failures.slice(0, 2).map((f) => f.reason ?? 'Unknown error').join('；')}${summary.failures.length > 2 ? '；...' : ''}` : ''
          })`
        : ''
    throw new Error((err.error ?? `HTTP ${res.status}`) + summaryText)
  }
  return json
}

export const api = {
  login: (username: string, password: string) =>
    apiFetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }) as Promise<{ user: { id: string; username: string; role: string } }>,
  logout: () => apiFetch('/api/auth/logout', { method: 'POST' }),
  me: () => apiFetch('/api/auth/me') as Promise<{ user: { id: string; username: string; role: string } | null }>,
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    }) as Promise<{ ok: boolean }>,
  createUser: (body: { username: string; password: string; role: 'admin' | 'super_admin' }) =>
    apiFetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  listAdmins: () =>
    apiFetch('/api/users/admins') as Promise<{ admins: Array<{ id: string; username: string; role: string }> }>,
  resetUserPassword: (userId: string, newPassword: string) =>
    apiFetch(`/api/users/${userId}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newPassword }),
    }) as Promise<{ ok: boolean }>,
  events: () => apiFetch('/api/events') as Promise<{ events: EventRow[] }>,
  createEvent: (title: string, slug?: string) =>
    apiFetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, slug }),
    }) as Promise<{ event: { id: string; slug: string; title: string } }>,
  event: (id: string) => apiFetch(`/api/events/${id}`) as Promise<{ event: EventRow }>,
  patchEvent: (eventId: string, body: { title: string }) =>
    apiFetch(`/api/events/${eventId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  stages: (eventId: string) => apiFetch(`/api/events/${eventId}/stages`) as Promise<{ stages: StageRow[] }>,
  eventAdmins: (eventId: string) =>
    apiFetch(`/api/events/${eventId}/admins`) as Promise<{
      admins: Array<{
        assignmentId: string
        userId: string
        username: string
        role: string
        assignedBy: string
        createdAt: string | number | Date
      }>
    }>,
  assignEventAdmin: (eventId: string, userId: string) =>
    apiFetch(`/api/events/${eventId}/admins`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    }),
  unassignEventAdmin: (eventId: string, userId: string) =>
    apiFetch(`/api/events/${eventId}/admins/${userId}`, { method: 'DELETE' }),
  createStage: (
    eventId: string,
    body: { kind: string; round?: number; sortIndex?: number }
  ) =>
    apiFetch(`/api/events/${eventId}/stages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }) as Promise<{ stage: { id: string } }>,
  patchStage: (stageId: string, body: Record<string, unknown>) =>
    apiFetch(`/api/stages/${stageId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  deleteStage: (stageId: string) => apiFetch(`/api/stages/${stageId}`, { method: 'DELETE' }),
  publishStage: (stageId: string) => apiFetch(`/api/stages/${stageId}/publish`, { method: 'POST' }),
  unpublishStage: (stageId: string) => apiFetch(`/api/stages/${stageId}/unpublish`, { method: 'POST' }),
  uploadUrl: (stageId: string) =>
    apiFetch(`/api/stages/${stageId}/upload-url`, { method: 'POST' }) as Promise<{
      method: string
      path: string
      expiresInSec: number
    }>,
  parseStage: (stageId: string) => apiFetch(`/api/stages/${stageId}/parse`, { method: 'POST' }) as Promise<{
    ok: boolean
    parsed: unknown
    parts: number
    summary: {
      total: number
      succeeded: number
      failed: number
      successes?: Array<{ uploadId: string | null; key: string; model: string }>
      failures?: Array<{ uploadId: string | null; key: string; reason: string }>
    }
  }>,
  parseProgress: (stageId: string) =>
    apiFetch(`/api/stages/${stageId}/parse-progress`) as Promise<{
      running: boolean
      done: number
      total: number
      succeeded: number
      failed: number
    }>,
  stageUploads: (stageId: string) =>
    apiFetch(`/api/stages/${stageId}/uploads`) as Promise<{
      uploads: Array<{ id: string; createdAt: number; contentType: string | null; sizeBytes: number | null; sortIndex: number }>
    }>,
  deleteUpload: (stageId: string, uploadId: string) =>
    apiFetch(`/api/stages/${stageId}/uploads/${uploadId}`, { method: 'DELETE' }),
  patchUpload: (stageId: string, uploadId: string, body: { sortIndex?: number }) =>
    apiFetch(`/api/stages/${stageId}/uploads/${uploadId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  reorderUploads: (stageId: string, ids: string[]) =>
    apiFetch(`/api/stages/${stageId}/uploads/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    }),
  publicEvent: (slug: string) =>
    apiFetch(`/api/public/events/${slug}`) as Promise<{
      event: { id: string; slug: string; title: string; updatedAt: string | number | Date }
      stages: PublicStage[]
    }>,
  publicStage: (slug: string, stageId: string) =>
    apiFetch(`/api/public/events/${slug}/stages/${stageId}`) as Promise<{
      stage: { id: string; kind: string; round: number | null; sortIndex: number; parsed: unknown; displayMode: string; uploads?: Array<{ id: string }> }
    }>,
}

export type EventRow = {
  id: string
  slug: string
  title: string
  createdBy: string
  createdAt: string | number | Date
  updatedAt: string | number | Date
}

export type StageRow = {
  id: string
  eventId: string
  kind: string
  round: number | null
  sortIndex: number
  status: string
  parsedJson: string | null
  parseError: string | null
  r2Key: string | null
  uploadCount?: number
  parserModel: string | null
  showImagesOnly: boolean
  displayMode: string
  updatedAt: string | number | Date
}

export type PublicStage = {
  id: string
  kind: string
  round: number | null
  sortIndex: number
  status: string
  updatedAt: string | number | Date
}

export async function putUpload(path: string, file: File) {
  const res = await fetch(`${base}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((json as { error?: string }).error ?? `HTTP ${res.status}`)
  return json as { ok: boolean; r2Key: string }
}
